import { api, TCustomAxiosPaginationResponse, TCustomAxiosResponse } from '..';
import { MIN_CHARS_TO_DO_SEARCH, SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';
import { apiPaths } from '@/services/api/paths';
import {
  TContent,
  TContentAuditLogEntry,
  TContentDetail,
  TContentListItem,
  TContentStatusFilter,
  TContentType,
  TContentVisibilityFilter,
  TCreateBrandedContentPayload,
  TUpdateContentPayload,
} from '@/types/Content.types';

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' } as const;


export type TGetAdminContentListParams = {
  page: number;
  // Free-text search (`q`) over title + author name/email.
  filterText: string;
  status: TContentStatusFilter;
  type?: TContentType;
  visibility?: TContentVisibilityFilter;
  isPlatformContent?: boolean;
  // Backend `sort` format: `<field>:<asc|desc>` (e.g. `publishedAt:desc`).
  sort?: string;
};

export const getAdminContentList = async ({
  page,
  filterText,
  status,
  type,
  visibility,
  isPlatformContent,
  sort,
}: TGetAdminContentListParams): Promise<TCustomAxiosPaginationResponse<TContentListItem[]>> => {
  const params: { [key: string]: string | number | boolean } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
    status,
  };

  if (type) {
    params.type = type;
  }

  // `all` is a UI-only sentinel — only send a concrete visibility filter to the backend.
  if (visibility && visibility !== 'all') {
    params.visibility = visibility;
  }

  if (isPlatformContent !== undefined) {
    params.isPlatformContent = isPlatformContent;
  }

  if (sort) {
    params.sort = sort;
  }

  if (filterText && filterText.length >= MIN_CHARS_TO_DO_SEARCH) {
    params.q = filterText;
  }

  const { data } = await api.get<TCustomAxiosPaginationResponse<TContentListItem[]>>(
    apiPaths.content.adminRoot,
    { params },
  );

  return data;
};

export const getAdminContentDetail = async (id: string): Promise<TContentDetail> => {
  const { data } = await api.get(apiPaths.content.adminById(id));

  return data.data as TContentDetail;
};

export const getAdminContentAuditLog = async (
  id: string,
  page: number,
): Promise<TCustomAxiosPaginationResponse<TContentAuditLogEntry[]>> => {
  const { data } = await api.get<TCustomAxiosPaginationResponse<TContentAuditLogEntry[]>>(
    apiPaths.content.adminAuditLog(id),
    { params: { page, limit: SUPER_ADMIN_PAGE_SIZE } },
  );

  return data;
};

export const removeAdminContent = async (
  id: string,
  removalReason: string,
): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.adminRemove(id), { removalReason });

  return data;
};

export const restoreAdminContent = async (id: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.adminRestore(id), {});

  return data;
};

// --- Branded (MyOmnia platform) content lifecycle ---

// Wrapper create — server forces isPlatformContent=true + public visibility. Returns a draft.
export const createBrandedContent = async (
  payload: TCreateBrandedContentPayload,
): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.adminMyOmnia, payload);

  return data;
};

export const getContentById = async (id: string): Promise<TContent> => {
  const { data } = await api.get(apiPaths.content.byId(id));

  return data.data as TContent;
};

export const updateContent = async (
  id: string,
  payload: TUpdateContentPayload,
): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.patch(apiPaths.content.byId(id), payload);

  return data;
};

export const publishContent = async (id: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.publish(id), {});

  return data;
};

export const unpublishContent = async (id: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.unpublish(id), {});

  return data;
};

export const deleteContent = async (id: string): Promise<TCustomAxiosResponse<null>> => {
  const { data } = await api.delete(apiPaths.content.byId(id));

  return data;
};

export const uploadContentThumbnail = async (
  id: string,
  file: File,
): Promise<TCustomAxiosResponse<TContent>> => {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await api.post(apiPaths.content.thumbnail(id), formData, {
    headers: MULTIPART_HEADERS,
  });

  return data;
};

// --- Video sources ---

// Issues a Mux direct-upload URL for a draft video; the browser uploads the file to it.
export const createMuxUploadUrl = async (
  id: string,
): Promise<{ uploadId: string; uploadUrl: string }> => {
  const { data } = await api.post(apiPaths.content.videoMuxUploadUrl(id), {});

  return data.data as { uploadId: string; uploadUrl: string };
};

export const setYoutubeVideoSource = async (
  id: string,
  url: string,
): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.videoYoutube(id), { url });

  return data;
};

export const uploadArticleImage = async (
  id: string,
  file: File,
  sectionId: string,
  subsectionId?: string,
): Promise<TCustomAxiosResponse<TContent>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sectionId', sectionId);
  if (subsectionId) {
    formData.append('subsectionId', subsectionId);
  }

  const { data } = await api.post(apiPaths.content.articleImage(id), formData, {
    headers: MULTIPART_HEADERS,
  });

  return data;
};
